import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from 'helpers/hooks/useFetch';
import 'styles/systemManager.scss';
import {
    BackendAvailability,
    BackendKey,
    DatabaseCardProps,
    DatabaseHealth,
    DatabaseHealthData,
    DatabaseHealthResponse,
    Health,
    HealthCheck,
    HealthKpiCardProps,
    HealthRequest,
    HealthStatus,
    MetricProps,
    ServiceHealth,
    ServiceHealthData
} from 'models/systemManager/health';
import { HSL, MAINTENANCES_REPORTS, REPAIR_CENTER, SPARE_PARTS, USER_MANAGER } from 'resources';
import { Icon } from '@scuf/common';

const SystemManager: React.FC = () => {
    const { t, i18n } = useTranslation(['systemManager', 'common']);

    const [healthCount, setHealthCount] = useState<number>(0);
    const [warningCount, setWarningCount] = useState<number>(0);
    const [unhealthyCount, setUnhealthyCount] = useState<number>(0);
    const [totalChecks, setTotalChecks] = useState<number>(5);
    const [healthPercentage, setHealthPercentage] = useState<number>(0);
    const [generalStatus, setGeneralStatus] = useState<HealthStatus>('Healthy');

    const [environment, setEnvironment] = useState<string>('-');
    const [server, setServer] = useState<string>('-');
    const [lastCheckedAt, setLastCheckedAt] = useState<string>('-');

    const [checkMicrosiga, setCheckMicrosiga] = useState<ServiceHealth | null>(null);
    const [databases, setDatabases] = useState<DatabaseHealth[]>([]);

    const INITIAL_BACKEND_AVAILABILITY: BackendAvailability = {
        user: false,
        spare: false,
        rma: false,
        hsl: false,
        maintenance: false
    };

    const [backendAvailability, setBackendAvailability] = useState<BackendAvailability>(INITIAL_BACKEND_AVAILABILITY);

    const [healthUser, getHealthUser, loadingGetHealthUser] = useFetch<null, DatabaseHealthResponse>({
        url: USER_MANAGER.checkHealth,
        method: 'GET'
    });

    const [healthSpare, getHealthSpare, loadingGetHealthSpare] = useFetch<null, DatabaseHealthResponse>({
        url: SPARE_PARTS.checkHealth,
        method: 'GET'
    });

    const [healthRepair, getHealthRepair, loadingGetHealthRepair] = useFetch<null, DatabaseHealthResponse>({
        url: REPAIR_CENTER.checkHealth,
        method: 'GET'
    });

    const [healthHsl, getHealthHsl, loadingGetHealthHsl] = useFetch<null>({
        url: HSL.checkHealth,
        method: 'GET'
    });

    const [healtMaintanance, getHealthMaintanance, loadingGetHealthMaintanance] = useFetch<null, any>({
        url: MAINTENANCES_REPORTS.checkHealth,
        method: 'GET'
    });

    const isLoading = useMemo(
        () =>
            loadingGetHealthUser ||
            loadingGetHealthSpare ||
            loadingGetHealthRepair ||
            loadingGetHealthHsl ||
            loadingGetHealthMaintanance,
        [loadingGetHealthUser, loadingGetHealthSpare, loadingGetHealthRepair, loadingGetHealthHsl, loadingGetHealthMaintanance]
    );

    const fetchData = useCallback(async () => {
        await Promise.all([
            safeRequestHealth('user', 'User', getHealthUser),
            safeRequestHealth('spare', 'Spare', getHealthSpare),
            safeRequestHealth('rma', 'Rma', getHealthRepair),
            safeRequestHealth('hsl', 'HSL', getHealthHsl),
            safeRequestHealth('maintenance', 'Maintenance', getHealthMaintanance)
        ]);
    }, []);

    const setBackendStatus = useCallback((backendKey: BackendKey, isAvailable: boolean) => {
        setBackendAvailability(current => ({
            ...current,
            [backendKey]: isAvailable
        }));
    }, []);

    const safeRequestHealth = useCallback(
        async (backendKey: BackendKey, backendName: string, request: HealthRequest) => {
            let hasValidResponse = false;

            try {
                await request(null, (current: any) => {
                    const payload = getPayload(current) ?? getPayload({ data: current });

                    if (payload) {
                        hasValidResponse = true;
                        console.log(`Deu certo a requisição ${backendName}:`, payload);
                        setBackendStatus(backendKey, true);
                    } else {
                        hasValidResponse = false;
                        console.log(`Backend ${backendName} respondeu sem payload válido:`, current);
                        setBackendStatus(backendKey, false);
                    }
                });

                if (!hasValidResponse) {
                    console.log(`Backend ${backendName} não retornou dados válidos.`);
                    setBackendStatus(backendKey, false);
                }
            } catch (error) {
                console.log(`Erro ao buscar saúde do backend ${backendName}:`, error);
                setBackendStatus(backendKey, false);
            }
        },
        [setBackendStatus]
    );

    useEffect(() => {
        fetchData();

        const interval = setInterval(fetchData, 100000);

        return () => clearInterval(interval);
    }, [fetchData]);

    useEffect(() => {
        const backendHealthList = [
            {
                key: 'user' as BackendKey,
                displayName: 'User',
                health: backendAvailability.user ? getPayload(healthUser) : null
            },
            {
                key: 'spare' as BackendKey,
                displayName: 'Spare',
                health: backendAvailability.spare ? getPayload(healthSpare) : null
            },
            {
                key: 'rma' as BackendKey,
                displayName: 'Rma',
                health: backendAvailability.rma ? getPayload(healthRepair) : null
            },
            {
                key: 'hsl' as BackendKey,
                displayName: 'HSL',
                health: backendAvailability.hsl ? getPayload(healthHsl) : null
            },
            {
                key: 'maintenance' as BackendKey,
                displayName: 'Maintenance',
                health: backendAvailability.maintenance ? getPayload(healtMaintanance) : null
            }
        ];

        const userHealth = backendHealthList.find(backend => backend.key === 'user')?.health ?? null;

        const allDatabases = backendHealthList.map(backend =>
            buildDatabaseHealthFromBackend(backend.displayName, backend.health)
        );

        const microsigaCheck = findCheck(userHealth, 'microsiga');

        const microsiga = buildMicrosigaHealth(userHealth, microsigaCheck);

        const allChecks = [...allDatabases, microsiga];

        const healthy = allChecks.filter(check => check.status === 'Healthy').length;
        const warning = allChecks.filter(check => check.status === 'Warning').length;
        const unhealthy = allChecks.filter(check => check.status === 'Unhealthy').length;
        const total = allChecks.length;
        const percentage = total === 0 ? 0 : Math.round((healthy / total) * 100);

        const firstAvailableHealth = backendHealthList.find(backend => backend.health)?.health ?? null;

        setDatabases(allDatabases);
        setCheckMicrosiga(microsiga);
        setHealthCount(healthy);
        setWarningCount(warning);
        setUnhealthyCount(unhealthy);
        setTotalChecks(total);
        setHealthPercentage(percentage);
        setEnvironment(firstAvailableHealth?.environment || '-');
        setServer(firstAvailableHealth?.server || '-');
        setLastCheckedAt(formatDate(firstAvailableHealth?.checkedAt, i18n.language));

        if (unhealthy > 0) {
            setGeneralStatus('Unhealthy');
        } else if (warning > 0) {
            setGeneralStatus('Warning');
        } else {
            setGeneralStatus('Healthy');
        }
    }, [healthUser, healthSpare, healthRepair, healthHsl, i18n.language, healtMaintanance, backendAvailability]);

    return (
        <div className="system-health-page">
            <header className="health-header">
                <div>
                    <span className="eyebrow">{t('SystemMonitor')}</span>
                    <h1>{t('SystemHealth')}</h1>
                    <p>{t('Monitoring')}</p>

                    {isLoading && (
                        <div className="top-loading-indicator">
                            <span className="loading-spinner" />
                            <span>{t('UpdatingData')}</span>
                        </div>
                    )}
                </div>

                <div className={`general-health-card ${getStatusClass(generalStatus)}`}>
                    <span className="general-label">{t('GeneralHealth')}</span>
                    <strong>{healthPercentage}%</strong>
                    <span>{t(getStatusLabel(generalStatus))}</span>
                </div>
            </header>

            <section className="environment-bar">
                <div>
                    <span>{t('Environment')}</span>
                    <strong>{environment}</strong>
                </div>

                <div>
                    <span>{t('Server')}</span>
                    <strong>{server}</strong>
                </div>

                <div>
                    <span>{t('LastCheck')}</span>
                    <strong>{lastCheckedAt}</strong>
                </div>

                <div>
                    <span>{t('Update')}</span>
                    <Icon
                        size="large"
                        root="common"
                        name="refresh"
                        className="icon-event"
                        onClick={() => {
                            if (
                                loadingGetHealthHsl ||
                                loadingGetHealthRepair ||
                                loadingGetHealthSpare ||
                                loadingGetHealthUser ||
                                loadingGetHealthMaintanance
                            )
                                return;
                            fetchData();
                        }}
                    />
                </div>
            </section>

            {isLoading && databases.length === 0 ? (
                []
            ) : (
                <>
                    <section className="kpi-grid">
                        <HealthKpiCard
                            title={t('UpServices')}
                            value={healthCount}
                            description={t('HealthyChecks')}
                            variant="success"
                        />

                        <HealthKpiCard
                            title={t('Warnings')}
                            value={warningCount}
                            description={t('NecessaryAttention')}
                            variant="warning"
                        />

                        <HealthKpiCard
                            title={t('DownServices')}
                            value={unhealthyCount}
                            description={t('Unavailable')}
                            variant="danger"
                        />

                        <HealthKpiCard
                            title={t('TotalMonitored')}
                            value={totalChecks}
                            description={t('Db+Microsiga')}
                            variant="neutral"
                        />
                    </section>

                    <section className="section-block">
                        <div className="section-title">
                            <div>
                                <h2>{t('Batabases')}</h2>
                                <p>{t('Status4Db')}</p>
                            </div>
                        </div>

                        <div className="database-grid">
                            {databases.map(database => (
                                <DatabaseCard key={database.databaseName} database={database} />
                            ))}
                        </div>
                    </section>

                    <section className="section-block">
                        <div className="section-title">
                            <div>
                                <h2>{t('Microsiga')}</h2>
                                <p>{t('MonitoringMicrosiga')}</p>
                            </div>
                        </div>

                        {checkMicrosiga ? (
                            <div className={`microsiga-card ${getStatusClass(checkMicrosiga.status)}`}>
                                <div className="service-info">
                                    <div className="status-dot-wrapper">
                                        <span className={`status-dot ${getStatusClass(checkMicrosiga.status)}`} />
                                    </div>

                                    <div>
                                        <h3>{checkMicrosiga.name}</h3>
                                        <p>{checkMicrosiga.description}</p>
                                    </div>
                                </div>

                                <div className="latency-box">
                                    <span>{t('Latency')}</span>
                                    <strong>{checkMicrosiga.responseTimeMs} ms</strong>
                                    <small>{t(getLatencyLabel(checkMicrosiga.responseTimeMs))}</small>
                                </div>
                            </div>
                        ) : (
                            <p>{t('MicrosigaNotReturned')}</p>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

const HealthKpiCard: React.FC<HealthKpiCardProps> = ({ title, value, description, variant }) => {
    return (
        <div className={`kpi-card ${variant}`}>
            <span>{title}</span>
            <strong>{value}</strong>
            <p>{description}</p>
        </div>
    );
};

const DatabaseCard: React.FC<DatabaseCardProps> = ({ database }) => {
    const { t } = useTranslation('systemManager');

    const maxConnections = database.maxConnections || 0;

    const connectionPercentage = maxConnections === 0 ? 0 : Math.round((database.usedConnections / maxConnections) * 100);

    return (
        <article className={`database-card ${getStatusClass(database.status)}`}>
            <div className="database-card-header">
                <div>
                    <h3>{database.name}</h3>
                    <p>{database.databaseName}</p>
                </div>

                <span className={`status-pill ${getStatusClass(database.status)}`}>{t(getStatusLabel(database.status))}</span>
            </div>

            <div className="database-metrics">
                <Metric label={t('Latency')} value={`${database.responseTimeMs} ms`} />

                <Metric label={t('Connections')} value={`${database.usedConnections} / ${database.maxConnections}`} />

                <Metric label={t('Tables')} value={String(database.tablesNames.length)} />

                <Metric label={t('Engine')} value={database.database} />
            </div>

            <div className="connection-progress">
                <div className="progress-header">
                    <span>{t('UseConnections')}</span>
                    <strong>{connectionPercentage}%</strong>
                </div>

                <div className="progress-track">
                    <div
                        className={`progress-fill ${getConnectionClass(connectionPercentage)}`}
                        style={{ width: `${connectionPercentage}%` }}
                    />
                </div>
            </div>

            <div className="database-footer">
                <span>{database.description}</span>
                <small>{t(getLatencyLabel(database.responseTimeMs))}</small>
            </div>

            <details className="tables-collapse">
                <summary>
                    <span>{t('TablesFound')}</span>
                    <strong>{database.tablesNames.length}</strong>
                </summary>

                <div className="tables-block">
                    <div className="tables-list">
                        {database.tablesNames.map(tableName => (
                            <span key={`${database.databaseName}-${tableName}`}>{tableName}</span>
                        ))}
                    </div>
                </div>
            </details>
        </article>
    );
};

const Metric: React.FC<MetricProps> = ({ label, value }) => {
    return (
        <div className="metric">
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
};

function getPayload(response: any): Health | null {
    const raw = response?.data?.data ?? response?.data ?? null;

    if (!raw) return null;

    const checks = raw.checks ?? raw.Checks;

    if (!checks) return null;

    return {
        health: raw.health ?? raw.Health ?? 0,
        unhealth: raw.unhealth ?? raw.Unhealth ?? 0,
        status: raw.status ?? raw.Status ?? 'Healthy',
        server: raw.server ?? raw.Server ?? '-',
        environment: raw.environment ?? raw.Environment ?? '-',
        checkedAt: raw.checkedAt ?? raw.CheckedAt,
        percentage: raw.percentage ?? raw.Percentage ?? 0,
        checks: checks.map((check: any) => ({
            name: check.name ?? check.Name,
            status: check.status ?? check.Status,
            description: check.description ?? check.Description,
            data: check.data ?? check.Data
        }))
    };
}

function findCheck(health: Health | null, checkName: string): HealthCheck | undefined {
    return health?.checks?.find(check => check.name.toLowerCase() === checkName.toLowerCase());
}

function toDatabaseHealth(check: HealthCheck, parentHealth: Health, fallbackName = 'Database'): DatabaseHealth {
    const data = check.data as DatabaseHealthData | undefined;

    return {
        name: data?.databaseName || fallbackName,
        status: check.status || 'Unhealthy',
        description: check.description || `Banco do backend ${fallbackName} não está saudável.`,
        database: data?.database || '-',
        serverName: data?.serverName || '-',
        databaseName: data?.databaseName || `${fallbackName} Database`,
        productVersion: data?.productVersion || '-',
        usedConnections: Number(data?.usedConnections ?? 0),
        maxConnections: Number(data?.maxConnections ?? 0),
        tablesNames: data?.tablesNames ?? [],
        responseTimeMs: Number(data?.responseTimeMs ?? 0),
        environment: parentHealth.environment,
        server: parentHealth.server,
        checkedAt: parentHealth.checkedAt
    };
}
function toServiceHealth(check: HealthCheck, parentHealth: Health): ServiceHealth {
    const data = check.data as ServiceHealthData;

    return {
        name: check.name,
        status: check.status,
        description: check.description,
        responseTimeMs: Number(data.responseTimeMs ?? 0),
        environment: parentHealth.environment,
        server: parentHealth.server,
        checkedAt: parentHealth.checkedAt
    };
}

function getStatusClass(status?: HealthStatus): string {
    switch (status) {
        case 'Healthy':
            return 'healthy';
        case 'Warning':
            return 'warning';
        case 'Unhealthy':
            return 'unhealthy';
        default:
            return 'healthy';
    }
}

function getStatusLabel(status?: HealthStatus): string {
    switch (status) {
        case 'Healthy':
            return 'Healthy';
        case 'Warning':
            return 'Warning';
        case 'Unhealthy':
            return 'Down';
        default:
            return 'Healthy';
    }
}

function getConnectionClass(percentage: number): string {
    if (percentage >= 80) return 'danger';
    if (percentage >= 60) return 'warning';

    return 'success';
}

function getLatencyLabel(responseTimeMs: number): string {
    if (responseTimeMs <= 500) return 'Excellent';
    if (responseTimeMs <= 1500) return 'Acceptable';
    if (responseTimeMs <= 3000) return 'Slow';

    return 'Critical';
}

function formatDate(date?: string, language = 'pt-BR'): string {
    if (!date) return '-';

    return new Date(date).toLocaleString(language, {
        dateStyle: 'short',
        timeStyle: 'medium'
    });
}

function buildDatabaseHealthFromBackend(backendName: string, health: Health | null): DatabaseHealth {
    if (!health) {
        return createUnavailableDatabaseHealth(
            backendName,
            `Backend ${backendName} indisponível. Não foi possível validar a saúde do banco.`
        );
    }

    const dbCheck = findCheck(health, 'db');

    if (!dbCheck) {
        return createUnavailableDatabaseHealth(
            backendName,
            `Backend ${backendName} respondeu, mas não retornou o check do banco.`
        );
    }

    return toDatabaseHealth(dbCheck, health, backendName);
}

function createUnavailableDatabaseHealth(backendName: string, description: string): DatabaseHealth {
    return {
        name: backendName,
        status: 'Unhealthy',
        description,
        database: '-',
        serverName: '-',
        databaseName: `${backendName} Backend`,
        productVersion: '-',
        usedConnections: 0,
        maxConnections: 0,
        tablesNames: [],
        responseTimeMs: 0,
        environment: '-',
        server: '-',
        checkedAt: undefined
    };
}

function buildMicrosigaHealth(userHealth: Health | null, microsigaCheck?: HealthCheck): ServiceHealth {
    if (!userHealth) {
        return {
            name: 'Microsiga',
            status: 'Unhealthy',
            description: 'Não foi possível validar o Microsiga porque o backend User está indisponível.',
            responseTimeMs: 0,
            environment: '-',
            server: '-',
            checkedAt: undefined
        };
    }

    if (!microsigaCheck) {
        return {
            name: 'Microsiga',
            status: 'Unhealthy',
            description: 'Backend User respondeu, mas não retornou o check do Microsiga.',
            responseTimeMs: 0,
            environment: userHealth.environment,
            server: userHealth.server,
            checkedAt: userHealth.checkedAt
        };
    }

    return toServiceHealth(microsigaCheck, userHealth);
}

export default SystemManager;
